<script setup lang="ts">
import PieChartStatistic from '@/shared/components/analytics/PieChartStatistic.vue'
import DateRangePicker from '@/shared/components/form/DateRangePicker.vue'
import InternalEmployerSelect from '@/shared/components/form/InternalEmployerSelect.vue'
import SalesPeriodOverviewTable from '@/features/sales/components/SalesPeriodOverviewTable.vue'
import {
  buildStatisticsRangePresets,
  endOfIsoWeek,
  formatLeadTimeDays,
  getSourceLabel,
  getSourceSeverity,
  startOfIsoWeek,
  toDateOnlyString,
} from '@/features/sales/composables/useSalesMetricsAdapter'
import { useAdminGetSalesStats } from '@swipe4work/api-orval'
import type { GetSalesStatsSalesPersonStatsDto, GetSalesStatsSourceStatsDto } from '@swipe4work/models-orval'

definePage({
  path: '/sales/sales-metrics/statistics',
  name: '/sales/SalesMetrics/statistics',
  meta: {
    permission: {
      resource: 'SalesMetrics',
      level: 'Read',
    },
  },
})

const rangePresets = buildStatisticsRangePresets()
const today = new Date()

const dateRange = ref<{
  from: Date | null,
  to: Date | null
} | null>({
  from: startOfIsoWeek(today),
  to: endOfIsoWeek(today),
})
const salesPersonId = ref<number | null>(null)

const statsParams = computed(() => ({
  salesPersonId: salesPersonId.value ?? undefined,
  dateFrom: dateRange.value?.from ? (toDateOnlyString(dateRange.value.from) as unknown as Date) : undefined,
  dateTo: dateRange.value?.to ? (toDateOnlyString(dateRange.value.to) as unknown as Date) : undefined,
}))

const { data, isPending, isError } = useAdminGetSalesStats(statsParams)

const stats = computed(() => data.value)

const chartColors = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#f97316',
  '#84cc16',
]

const demosPieItems = computed(() =>
  (stats.value?.perSalesPerson ?? []).map((p: GetSalesStatsSalesPersonStatsDto, i: number) => ({
    label: p.salesPersonName,
    count: p.plannedDemos,
    color: chartColors[i % chartColors.length],
  })),
)

const sourceRows = computed<GetSalesStatsSourceStatsDto[]>(() => stats.value?.perSource ?? [])

const sourcePieItems = computed(() =>
  sourceRows.value.map((row: GetSalesStatsSourceStatsDto, i: number) => ({
    label: getSourceLabel(row.source, row.sourceOther),
    count: row.demos,
    color: chartColors[i % chartColors.length],
  })),
)
</script>

<template>
  <div class="space-y-4">
    <div class="flex flex-wrap items-end justify-between gap-4">
      <div class="flex flex-wrap items-end gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-surface-500 font-medium tracking-wide uppercase">Periode</label>
          <DateRangePicker v-model="dateRange" :presets="rangePresets" placeholder="Alle data" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-surface-500 font-medium tracking-wide uppercase">Sales persoon</label>
          <InternalEmployerSelect v-model="salesPersonId" placeholder="Alle sales personen" />
        </div>
      </div>
    </div>

    <div v-if="isPending" class="space-y-4">
      <div class="grid grid-cols-2 gap-4 lg:grid-cols-8 sm:grid-cols-4">
        <Skeleton v-for="n in 8" :key="n" height="5rem" />
      </div>
      <Skeleton height="20rem" />
    </div>

    <Message v-else-if="isError" severity="error">
      Er is iets misgegaan bij het laden van statistieken.
    </Message>

    <template v-else>
      <div class="grid grid-cols-2 gap-4 lg:grid-cols-8 sm:grid-cols-4">
        <StatTile
          label="Totaal gebeld"
          :value="String(stats?.totalCalledCompanies ?? 0)"
        />
        <StatTile
          label="Totaal gesprekken"
          :value="String(stats?.totalConversations ?? 0)"
        />
        <StatTile
          label="Totaal demo's"
          :value="String(stats?.totalDemos ?? 0)"
          subtitle="Alle bronnen"
        />
        <StatTile
          label="Outbound demo's"
          :value="String(stats?.totalOutboundDemos ?? 0)"
          subtitle="Telt mee in de conversie"
        />
        <StatTile
          label="Deals Won"
          :value="String(stats?.totalDealsWon ?? 0)"
        />
        <StatTile
          label="Deals Verloren"
          :value="String(stats?.totalDealsLost ?? 0)"
        />
        <StatTile
          label="Conversie Bel→Gesprek"
          :value="`${(stats?.callToConversationRate ?? 0).toFixed(1)}%`"
        />
        <StatTile
          label="Conversie Gesprek→Demo"
          :value="`${(stats?.conversationToDemoRate ?? 0).toFixed(1)}%`"
          subtitle="Alleen outbound demo's"
        />
      </div>

      <div class="grid grid-cols-2 gap-4 lg:grid-cols-4 sm:grid-cols-2">
        <StatTile
          label="Demo → Win"
          :value="`${(stats?.demoToWinRate ?? 0).toFixed(1)}%`"
        />
        <StatTile
          label="Gem. demo → invite"
          :value="formatLeadTimeDays(stats?.avgDemoToInviteDays)"
        />
        <StatTile
          label="Gem. invite → bedrijf"
          :value="formatLeadTimeDays(stats?.avgOnboardingDays)"
        />
        <StatTile
          label="Gem. demo → bedrijf"
          :value="formatLeadTimeDays(stats?.avgDemoToCompanyDays)"
        />
      </div>

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PieChartStatistic
          title="Demo's per sales persoon"
          :items="demosPieItems"
          empty-message="Geen demo data beschikbaar."
        />
        <PieChartStatistic
          title="Demo's per bron"
          :items="sourcePieItems"
          empty-message="Geen demo data beschikbaar."
        />
      </div>

      <Card>
        <template #content>
          <div class="flex flex-col gap-4">
            <h3 class="text-lg font-semibold">
              Per Sales Persoon
            </h3>
            <DataTable
              :value="stats?.perSalesPerson ?? []"
              size="small"
              class="w-full"
            >
              <template #empty>
                <span class="text-sm text-surface-400">Geen data beschikbaar.</span>
              </template>
              <Column field="salesPersonName" header="Sales Persoon" />
              <Column field="calledCompanies" header="Gebeld" style="width: 6rem" />
              <Column field="conversations" header="Gesprekken" style="width: 7rem" />
              <Column field="plannedDemos" header="Demo's" style="width: 5rem" />
              <Column field="outboundDemos" header="Outbound" style="width: 6rem" />
              <Column field="dealsWon" header="Deals Won" style="width: 6rem" />
              <Column field="dealsLost" header="Deals Lost" style="width: 6rem" />
              <Column header="Bel→Gesprek" style="width: 8rem">
                <template #body="{ data: row }">
                  <span class="text-sm font-mono">{{ row.callToConversationRate.toFixed(1) }}%</span>
                </template>
              </Column>
              <Column header="Gesprek→Outbound demo" style="width: 11rem">
                <template #body="{ data: row }">
                  <span class="text-sm font-mono">{{ row.conversationToDemoRate.toFixed(1) }}%</span>
                </template>
              </Column>
              <Column header="Demo→Win" style="width: 7rem">
                <template #body="{ data: row }">
                  <span class="text-sm font-mono">{{ row.demoToWinRate.toFixed(1) }}%</span>
                </template>
              </Column>
            </DataTable>
          </div>
        </template>
      </Card>

      <Card>
        <template #content>
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1">
              <h3 class="text-surface-900 font-bold">
                Demo's per bron
              </h3>
              <p class="text-sm text-surface-500">
                Alle demo's in de gekozen periode, per kanaal. Alleen outbound demo's tellen mee in de
                conversie Gebeld → Gesprekken → Demo.
              </p>
            </div>
            <DataTable
              :value="sourceRows"
              size="small"
              class="w-full"
            >
              <template #empty>
                <span class="text-sm text-surface-400">Geen demo's in deze periode.</span>
              </template>
              <Column header="Bron">
                <template #body="{ data: row }">
                  <Tag
                    :value="getSourceLabel(row.source, row.sourceOther)"
                    :severity="getSourceSeverity(row.source)"
                  />
                </template>
              </Column>
              <Column field="demos" header="Demo's" style="width: 6rem" />
              <Column header="Aandeel" style="width: 7rem">
                <template #body="{ data: row }">
                  <span class="text-sm font-mono">
                    {{ (stats?.totalDemos ? row.demos / stats.totalDemos * 100 : 0).toFixed(1) }}%
                  </span>
                </template>
              </Column>
              <Column field="dealsWon" header="Deals Won" style="width: 6rem" />
              <Column field="dealsLost" header="Deals Lost" style="width: 6rem" />
              <Column header="Demo→Win" style="width: 7rem">
                <template #body="{ data: row }">
                  <span class="text-sm font-mono">{{ row.demoToWinRate.toFixed(1) }}%</span>
                </template>
              </Column>
            </DataTable>
          </div>
        </template>
      </Card>

      <SalesPeriodOverviewTable :stats="stats" default-group-by="week" />
    </template>
  </div>
</template>
